// src/components/Center Admin/layout/Sidebar.tsx
import React, { useRef, useState, useEffect } from "react";
import { ReactComponent as CalendarIcon } from "../../../assets/icons/calendar.svg";
import api from "../../../api/axiosConfig";
import "../../../center admin css/centerAdmin.css";

export type SidebarItem = {
  key: string;
  label: string;

  subLabel?: React.ReactNode;
  avatarUrl?: string;
  time?: string;
  fullData?: any;
};

type SidebarProps = {
  items: SidebarItem[];
  activeKey?: string;
  onSelect?: (key: string) => void;
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  isLoading?: boolean;
  noItemsMessage?: string;
  userName: string;
};

const Sidebar: React.FC<SidebarProps> = ({
  items,
  activeKey,
  onSelect,
  selectedDate,
  onDateChange,
  isLoading,
  noItemsMessage,
  userName,
}) => {
  const dateInputRef = useRef<HTMLInputElement | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [avatarMap, setAvatarMap] = useState<Record<string, string>>({});

  /* ------------------ 🔐 PRESIGNED IMAGE FETCH ------------------ */

  useEffect(() => {
    const fetchImages = async () => {
      const token = sessionStorage.getItem("accessToken");

      if (!token) return;

      const map: Record<string, string> = {};

      for (const item of items) {
        const filePath =
          item.fullData?.capture_photo ||
          item.fullData?.captured_photo ||
          item.fullData?.photo ||
          item.fullData?.child_profile ||
          item.avatarUrl ||
          "";

        if (!filePath) continue;

        try {
          // Already a presigned URL
          if (
            filePath.includes("X-Amz-Signature") ||
            filePath.includes("X-Amz-Credential")
          ) {
            map[item.key] = filePath;
            continue;
          }

          // Remove leading slash if present
          const cleanPath = filePath.startsWith("/")
            ? filePath.slice(1)
            : filePath;

          const parts = cleanPath.split("/");

          // Expected:
          // module-part-1/module-part-2/key/...
          if (parts.length < 3) {
            console.warn("Invalid Check-in profile key:", filePath);
            continue;
          }

          const module = `${parts[0]}/${parts[1]}`;
          const key = parts.slice(2).join("/");

          const response = await api.S3config.get("/view-url", {
            params: {
              module,
              key,
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          const signedUrl = response.data?.url || (response as any).url;

          if (signedUrl) {
            map[item.key] = signedUrl;
          }
        } catch (err) {
          console.error(`Check-in image fetch failed for ${item.label}`, err);
        }
      }

      setAvatarMap(map);
    };

    if (items.length > 0) {
      fetchImages();
    }
  }, [items]);

  /* -------------------------------------------------------------- */

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.valueAsDate) {
      const selected = e.target.valueAsDate;
      const adjustedDate = new Date(
        selected.valueOf() + selected.getTimezoneOffset() * 60000,
      );
      onDateChange(adjustedDate);
    }
  };

  const openCalendar = () => dateInputRef.current?.click();

  const filteredItems = items.filter((item) =>
    item.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const groupedItems = filteredItems.reduce(
    (acc: Record<string, SidebarItem[]>, item) => {
      const slot = item.time || "Unknown Slot";

      if (!acc[slot]) {
        acc[slot] = [];
      }

      acc[slot].push(item);

      return acc;
    },
    {},
  );

  return (
    <aside className="sidebar1">
      <div className="sidebar__header">
        <div className="sidebar__title">Welcome, {userName}</div>

        <div className="sidebar__date-wrapper">
          <div className="sidebar__datebox" onClick={openCalendar}>
            <CalendarIcon width={24} height={24} />
            {selectedDate.toLocaleDateString()}
          </div>
        </div>

        <input
          ref={dateInputRef}
          type="date"
          style={{ display: "none" }}
          value={selectedDate.toISOString().split("T")[0]}
          onChange={handleDateChange}
        />

        <input
          className="gc-queue-search"
          placeholder="Search patients..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <div className="sidebar__meta">
          <span>{isLoading ? "Loading..." : `${items.length} Records`}</span>
        </div>
      </div>

      <ul className="sidebar__list">
        {Object.entries(groupedItems).map(([slotTime, slotItems], index) => (
          <React.Fragment key={slotTime}>
            <li className="sidebar__slot-header">
              {/* <div className="sidebar__slot-title">Slot {index + 1}</div> */}
              <div className="sidebar__slot-title">
                {slotTime !== "Unknown Slot"
                  ? `Slot ${index + 1} · ${slotTime}`
                  : `Slot ${index + 1}`}
              </div>
            </li>

            {slotItems.map((item) => (
              <li key={item.key}>
                <button
                  className={`sidebar__item${
                    activeKey === item.key ? " sidebar__item--active" : ""
                  }${
                    item.fullData?.pathId
                      ? " sidebar__item--assigned"
                      : " sidebar__item--unassigned"
                  }`}
                  onClick={() => onSelect?.(item.key)}
                >
                  <div className="sidebar__left">
                    {item.time && (
                      <div className="sidebar__time">{item.time}</div>
                    )}

                    <div className="sidebar__text">
                      <div className="sidebar__label">{item.label}</div>

                      {item.subLabel && (
                        <div className="sidebar__sublabel">{item.subLabel}</div>
                      )}
                    </div>
                  </div>

                  {avatarMap[item.key] ? (
                    <img
                      src={avatarMap[item.key]}
                      className="sidebar__avatar"
                      alt=""
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <span className="sidebar__avatar sidebar__avatar--placeholder" />
                  )}
                </button>
              </li>
            ))}
          </React.Fragment>
        ))}
      </ul>
    </aside>
  );
};

export default Sidebar;
